import type { ReportTheme } from '../types/reportTheme';
import type { ReportTemplate } from '../types/reportTemplate';
import type { SiteEntity, ReportAudienceMode, SiteLayer } from '../types/site';
import { colorForCategory } from '../config/siteLayerColors';

export const DEFAULT_PRIMARY_COVER_LOGO_URL = '/datacrate-logo.svg';
/** Platform-default company wordmark used as the last-resort fallback for
 *  the primary-logo alt text when neither the report's template nor the
 *  base theme set a company name. */
export const DEFAULT_COMPANY_NAME = 'Datacrate';

export interface ResolvedReportBranding {
  audienceMode: ReportAudienceMode;
  siteWordmark: string;
  primaryLogoUrl?: string | null;
  secondaryLogoUrl?: string | null;
  primaryLogoAlt: string;
  secondaryLogoAlt: string;
  kmzLayers?: Array<Pick<SiteLayer, 'id' | 'name' | 'category' | 'geometry' | 'color'>>;
  layerIndicators?: Array<{ id: string; name: string; category: SiteLayer['category']; color: string }>;
}

/** Exact revision-owned customer mark handed to Office projectors. SVG decks
 * receive a server-rasterized PNG while DOCX retains the verified SVG bytes. */
export interface FrozenBrandingRenderAsset {
  bytes: Uint8Array;
  mediaType: 'image/png' | 'image/svg+xml';
  altText: string;
  pptxBytes: Uint8Array;
  pptxMediaType: 'image/png';
}

/**
 * The KMZ-layer overlay inputs the `HeroCoverBoundaryOverlay` consumes:
 * `kmzLayers` (drawn geometry) + `layerIndicators` (leader-line labels).
 * Extracted so any surface that wants the detailed boundary rendering —
 * the report cover AND the site page's SITE BOUNDARY panel — derives
 * these the same way from a site's `siteLayers` (single source of truth).
 */
export interface ResolvedSiteLayerOverlay {
  kmzLayers: Array<Pick<SiteLayer, 'id' | 'name' | 'category' | 'geometry' | 'color'>>;
  layerIndicators: Array<{ id: string; name: string; category: SiteLayer['category']; color: string }>;
}

/**
 * Build the overlay layer inputs from a site's `siteLayers`. Hidden
 * layers (`visible === false`) are dropped; indicator colours are
 * resolved through `colorForCategory` so category styling matches the
 * report cover exactly.
 */
export function resolveSiteLayerOverlay(
  site?: Pick<SiteEntity, 'siteLayers' | 'siteBoundaries' | 'siteBoundary'> | null,
): ResolvedSiteLayerOverlay {
  const siteLayerOverlays = (site?.siteLayers ?? [])
    .filter((layer) => layer.visible !== false)
    .map((layer) => ({
      id: layer.id,
      name: layer.name,
      category: layer.category,
      geometry: layer.geometry,
      color: layer.color,
    }));

  // MAP-FROM-KMZ FIX — the canonical SITE BOUNDARY lives in `siteBoundaries`
  // (MP-pack) / the deprecated flat `siteBoundary`, NOT necessarily in
  // `siteLayers`. A KMZ-imported or drawn parcel boundary therefore never
  // reached the report cover / site-overview overlay (both read ONLY
  // `siteLayers`), so the cover gate fell to "Site boundary — geometry not
  // available" even though the parcel polygon exists on the site (the user's
  // "no map" report). Derive a polygon overlay layer from the canonical
  // boundary so it always renders. Skipped when a polygon `siteLayer` already
  // carries the boundary (avoids a duplicate outline). The boundary is the BASE
  // geometry, so it is NOT added to `layerIndicators` (no leader-line label for
  // the parcel outline itself — only the named KMZ overlays get indicators).
  const boundaryLayers: ResolvedSiteLayerOverlay['kmzLayers'] = [];
  const hasPolygonLayer = siteLayerOverlays.some((l) => l.geometry?.type === 'polygon');
  if (!hasPolygonLayer) {
    const parcels = (site?.siteBoundaries ?? []).filter((p) => (p.polygon?.length ?? 0) >= 3);
    if (parcels.length > 0) {
      parcels.forEach((p, i) => {
        boundaryLayers.push({
          id: `site-boundary-${p.id ?? i}`,
          name: parcels.length > 1 ? `Parcel ${i + 1}` : 'Site Boundary',
          category: 'other',
          geometry: { type: 'polygon', coordinates: p.polygon },
          color: undefined,
        });
      });
    } else if ((site?.siteBoundary?.length ?? 0) >= 3) {
      boundaryLayers.push({
        id: 'site-boundary',
        name: 'Site Boundary',
        category: 'other',
        geometry: { type: 'polygon', coordinates: site!.siteBoundary! },
        color: undefined,
      });
    }
  }

  const kmzLayers = [...boundaryLayers, ...siteLayerOverlays];
  // Indicators (leader-line labels) cover only the NAMED KMZ overlays, never the
  // base parcel outline — so a boundary-only site gets a clean outline + no label.
  const layerIndicators = siteLayerOverlays.map((layer) => ({
    id: layer.id,
    name: layer.name,
    category: layer.category,
    color: colorForCategory(layer.category, layer.color),
  }));
  return { kmzLayers, layerIndicators };
}

/** First non-empty (trimmed) string, else undefined. */
function firstMeaningful(...values: Array<string | null | undefined>): string | undefined {
  for (const v of values) {
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return undefined;
}

export function resolveReportBranding(args: {
  site?: Pick<SiteEntity, 'id' | 'name' | 'reportAudienceDefault' | 'customerLogoUrl' | 'siteLayers'> | null;
  theme: Pick<ReportTheme, 'logoUrl' | 'companyName'>;
  /**
   * The template the report is rendered with, when known. The template's
   * own brand (`template.theme.companyName` / `template.theme.logoUrl`)
   * is AUTHORITATIVE for the report — it wins over the base theme so the
   * report reflects the brand configured on its template, not the
   * account-level default. Falls through to the base theme, then to the
   * platform default, only when the template hasn't set a value.
   */
  template?: Pick<ReportTemplate, 'theme'> | null;
  /**
   * The EFFECTIVE uploaded logo (user → org), resolved by
   * `resolveUploadedLogo` in `resolveEffectiveLogo.ts`. This is the
   * per-user / per-org brand logo set from the user menu (personal) or
   * the admin org section (org-wide). It slots into the existing brand
   * precedence AFTER the template + base theme but BEFORE the platform
   * default, so an explicit report-theme/template logo still wins, yet
   * an uploaded brand logo replaces the Datacrate default everywhere
   * the cover renders. Pass `null`/`undefined` when nothing is uploaded.
   * The cover consumes the result unchanged via
   * `branding.primaryLogoUrl ?? theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL`.
   */
  effectiveLogoUrl?: string | null;
}): ResolvedReportBranding {
  const audienceMode: ReportAudienceMode = args.site?.reportAudienceDefault ?? 'internal';
  // L-id-leak — never fall back to the raw Firestore `site.id` (an internal doc
  // id must never surface as the cover wordmark). Use the human placeholder.
  const siteWordmark = args.site?.name?.trim() || 'Unnamed site';

  // Brand precedence: template theme → base theme → effective uploaded
  // logo → platform default. The template's brand is authoritative so a
  // report shows the company name + logo configured on its template,
  // falling back to the base theme, then the per-user/per-org uploaded
  // brand logo (feat/org-logo), then the platform default ONLY when none
  // of the above is set.
  const templateLogoUrl = args.template?.theme?.logoUrl;
  const primaryLogoUrl =
    firstMeaningful(templateLogoUrl, args.theme.logoUrl, args.effectiveLogoUrl)
    ?? DEFAULT_PRIMARY_COVER_LOGO_URL;
  const companyName =
    firstMeaningful(args.template?.theme?.companyName, args.theme.companyName)
    ?? DEFAULT_COMPANY_NAME;

  const { kmzLayers, layerIndicators } = resolveSiteLayerOverlay(args.site ?? null);
  const secondaryLogoUrl =
    audienceMode === 'customer'
      ? (args.site?.customerLogoUrl ?? null)
      : null;

  return {
    audienceMode,
    siteWordmark,
    primaryLogoUrl,
    secondaryLogoUrl,
    primaryLogoAlt: companyName,
    secondaryLogoAlt: 'Customer logo',
    kmzLayers,
    layerIndicators,
  };
}

/**
 * REPORT-COVER ownership guard (2026-08-17, Pronghorn masthead RCA) — the
 * cover-path equivalent of `resolveSiteCardBranding`'s cross-company guard.
 *
 * A CROSS-ORG report (`report.companyId` ≠ viewer `companyId`) renders with
 * the viewer's session theme for COLORS, but the cover's BRAND (masthead
 * company name + logo) must never come from the viewer's session/base-theme
 * brand: when the creator's template brand cannot resolve (post-GOLDEN-LOCK
 * the stock template carries no brand at all), the old chain fell through to
 * `theme.companyName`/`logoUrl` — the VIEWER's personal reportTheme — so a
 * descendant org's report wore a stale third-party masthead ("Azimuth
 * Renewables" on a Pronghorn report). Brand precedence here:
 *
 *   1. Same-org / personal report (`reportCompanyId` unset or equal to the
 *      viewer's) → the established `template → theme → uploaded → default`
 *      chain, unchanged (`resolveReportBranding`).
 *   2. Cross-org report → the OWNING company's brand
 *      (`companies/{report.companyId}/settings/branding` + `.name`, via
 *      `companyBrandingFor`/`useCompanyBranding`), falling back to the
 *      NEUTRAL platform default. The viewer's template/theme/uploaded marks
 *      are all suppressed — unlike the card guard, the cover never falls
 *      back to the viewer's uploaded org/user logo, because the cover is the
 *      DOCUMENT's brand, not an ownership mark in the viewer's dashboard.
 *
 * Per-site CUSTOMER co-branding (`site.customerLogoUrl`) is site data and is
 * preserved in every branch. Frozen/immutable surfaces (`_frozenBranding`,
 * DOCX/PPTX/PDF artifact jobs) resolve from the generation-time presentation
 * snapshot and never enter this resolver.
 */
export function resolveReportCoverBranding(args: {
  site?: Pick<
    SiteEntity,
    'id' | 'name' | 'reportAudienceDefault' | 'customerLogoUrl' | 'siteLayers'
  > | null;
  theme: Pick<ReportTheme, 'logoUrl' | 'companyName'>;
  template?: Pick<ReportTemplate, 'theme'> | null;
  effectiveLogoUrl?: string | null;
  /** The report's owning company (`report.companyId`). Unset = personal/legacy. */
  reportCompanyId?: string | null;
  /** The signed-in viewer's company id (store `companyId`). Null = no org. */
  viewerCompanyId?: string | null;
  /**
   * The report's OWNING company brand (`report.companyId` →
   * `companyBrandingFor`). Null/undefined = not loaded / read denied — the
   * cross-org branch then falls to the neutral platform default.
   */
  owningCompanyBranding?: OwningCompanyBranding | null;
}): ResolvedReportBranding {
  const reportCompanyId = firstMeaningful(args.reportCompanyId);
  const viewerCompanyId = firstMeaningful(args.viewerCompanyId);
  if (reportCompanyId === undefined || reportCompanyId === viewerCompanyId) {
    return resolveReportBranding(args);
  }
  const owningLogoUrl = firstMeaningful(args.owningCompanyBranding?.logoUrl);
  const owningCompanyName = firstMeaningful(args.owningCompanyBranding?.companyName);
  return resolveReportBranding({
    site: args.site,
    theme: { logoUrl: owningLogoUrl ?? null, companyName: owningCompanyName ?? '' },
    template: null,
    effectiveLogoUrl: null,
  });
}

/**
 * OWNING-COMPANY brand for site/report CARDS + labels (2026-06-10,
 * supersedes the v1.3.2 suppress-only guard while keeping it as the
 * fallback). Resolved by `companyBrandingFor` / `useCompanyBranding`
 * (`src/lib/companyBranding.ts`) from `companies/{site.companyId}` +
 * its `settings/branding` doc.
 */
export interface OwningCompanyBranding {
  logoUrl?: string | null;
  companyName?: string | null;
}

/**
 * SITE-CARD / REPORT-CARD ownership branding (2026-06-10) — branding for
 * the compact dashboard SITE-LIST cards and the Reports-tab report cards.
 * Report COVERS keep using `resolveReportBranding` unchanged.
 *
 * The card is an OWNERSHIP mark, not a report-template preview, so the
 * primary mark must be the brand of the site's OWNING company
 * (`site.companyId`) — never the viewer's session/theme/template brand
 * (William's definitive branding model, supersedes the v1.3.2
 * suppress-only guard):
 *
 *   1. `site.companyId` set AND the owning company's brand resolved
 *      (`owningCompanyBranding`, from `companies/{cid}/settings/branding`
 *      + `companies/{cid}.name`) → the owning org's logo + name are the
 *      primary mark. Session template/theme/uploaded logos are ignored —
 *      a Clayco-owned site wears the Clayco mark for EVERY viewer.
 *   2. Owning brand unavailable (personal site, branding unset, or the
 *      cross-tenant read was rules-denied for a non-admin viewer) → the
 *      v1.3.2 guard behaviour: personal/own-company sites keep the
 *      established template → theme → uploaded → default session chain;
 *      cross-company sites suppress the session template/theme mark and
 *      fall back to the uploaded user/org brand, then the platform
 *      default — never a third company's template brand.
 *
 * Per-site CUSTOMER co-branding (`site.customerLogoUrl` on a
 * customer-audience site) is SITE data, not session data, so it is
 * preserved unchanged in every branch.
 */
export function resolveSiteCardBranding(args: {
  site?: Pick<
    SiteEntity,
    'id' | 'name' | 'reportAudienceDefault' | 'customerLogoUrl' | 'siteLayers' | 'companyId'
  > | null;
  theme: Pick<ReportTheme, 'logoUrl' | 'companyName'>;
  template?: Pick<ReportTemplate, 'theme'> | null;
  effectiveLogoUrl?: string | null;
  /** The signed-in viewer's company id (store `companyId`). Null = no org. */
  viewerCompanyId?: string | null;
  /** The viewer's company name — alt text for the fallback ownership mark. */
  viewerCompanyName?: string | null;
  /**
   * The SITE'S OWNING company brand (`site.companyId` →
   * `companyBrandingFor`). Null/undefined = not loaded / personal site /
   * read denied — the resolver then falls back to the guard behaviour.
   */
  owningCompanyBranding?: OwningCompanyBranding | null;
}): ResolvedReportBranding {
  const siteCompanyId = firstMeaningful(args.site?.companyId);
  const viewerCompanyId = firstMeaningful(args.viewerCompanyId);
  // Owning-company brand wins whenever the site HAS an owning company and
  // its brand resolved (logo and/or name) — for every viewer, including
  // members of the owning org itself.
  if (siteCompanyId !== undefined) {
    const owningLogoUrl = firstMeaningful(args.owningCompanyBranding?.logoUrl);
    const owningCompanyName = firstMeaningful(args.owningCompanyBranding?.companyName);
    if (owningLogoUrl !== undefined || owningCompanyName !== undefined) {
      return resolveReportBranding({
        site: args.site,
        theme: { logoUrl: owningLogoUrl ?? null, companyName: owningCompanyName ?? '' },
        template: null,
        // Only when the owning org has NO logo configured does the mark fall
        // back to the uploaded user/org brand → platform default (the same
        // chain as the guard branch below); the owning NAME still labels it.
        effectiveLogoUrl: owningLogoUrl !== undefined ? null : args.effectiveLogoUrl,
      });
    }
  }
  // Personal site (no companyId) or owned by the viewer's own company →
  // the session brand is attributable; keep the established
  // template → theme → uploaded → default chain.
  if (siteCompanyId === undefined || siteCompanyId === viewerCompanyId) {
    return resolveReportBranding(args);
  }
  // Cross-company site: never show another company's template/theme mark.
  return resolveReportBranding({
    site: args.site,
    theme: { logoUrl: null, companyName: args.viewerCompanyName ?? '' },
    template: null,
    effectiveLogoUrl: args.effectiveLogoUrl,
  });
}
